/* eslint-disable no-magic-numbers */
import {
  createdOverlayFileSchema,
  mapOverlaySchema,
  uploadedOverlayFileContentSchema,
} from '@/models/mapOverlaySchema';
import { axiosInstance, axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { CreatedOverlayFile, MapOverlay, OverlayGroup, PageOf } from '@/types/models';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { z } from 'zod';
import { PluginsEventBus } from '@/services/pluginsManager/pluginsEventBus';
import { showToast } from '@/utils/showToast';
import { ThunkConfig } from '@/types/store';
import { getError } from '@/utils/error-report/getError';
import axios from 'axios';
import { pageableSchema } from '@/models/pageableSchema';
import { overlayTypeSchema } from '@/models/configurationSchema';
import { removeOverlayBioEntityForGivenOverlay } from '@/redux/overlayBioEntity/overlayBioEntity.slice';
import { PROJECT_ID } from '@/constants';
import { apiPath } from '../apiPath';
import {
  CHUNK_SIZE,
  OVERLAYS_FETCHING_ERROR_PREFIX,
  USER_OVERLAYS_FETCHING_ERROR_PREFIX,
  USER_OVERLAY_ADD_ERROR_PREFIX,
  USER_OVERLAY_ADD_SUCCESS_MESSAGE,
  USER_OVERLAY_REMOVE_ERROR_PREFIX,
  USER_OVERLAY_REMOVE_SUCCESS_MESSAGE,
  USER_OVERLAY_UPDATE_ERROR_PREFIX,
  USER_OVERLAY_UPDATE_SUCCESS_MESSAGE,
} from './overlays.constants';
import { closeModal } from '../modal/modal.slice';
import type { RootState } from '../store';
import { OverlayType } from './overlays.types';

export const getOverlay = createAsyncThunk<MapOverlay | null, { overlayId: number }, ThunkConfig>(
  'overlays/getOverlay',
  async ({ overlayId }) => {
    try {
      const { data } = await axiosInstanceNewAPI.get<MapOverlay>(apiPath.getOverlay(overlayId), {
        withCredentials: true,
      });

      const isDataValid = validateDataUsingZodSchema(data, mapOverlaySchema);

      return isDataValid ? data : null;
    } catch (error) {
      return Promise.reject(getError({ error }));
    }
  },
);

export const getAllPublicOverlaysByProjectId = createAsyncThunk<MapOverlay[], string, ThunkConfig>(
  'overlays/getAllPublicOverlaysByProjectId',
  async (projectId: string) => {
    try {
      const response = await axiosInstanceNewAPI.get<PageOf<MapOverlay>>(
        apiPath.getAllOverlaysByProjectIdQuery(projectId, { publicOverlay: true }),
      );

      const isDataValid = validateDataUsingZodSchema(
        response.data,
        pageableSchema(mapOverlaySchema),
      );

      return isDataValid
        ? response.data.content.sort((overlayA, overlayB) => overlayA.order - overlayB.order)
        : [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: OVERLAYS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getAllProjectOverlays = createAsyncThunk<MapOverlay[], string, ThunkConfig>(
  'overlays/getAllProjectOverlays',
  async (projectId: string) => {
    try {
      const response = await axiosInstanceNewAPI.get<PageOf<MapOverlay>>(
        apiPath.getProjectOverlays(projectId),
        {
          withCredentials: true,
        },
      );

      const isDataValid = validateDataUsingZodSchema(
        response.data,
        pageableSchema(mapOverlaySchema),
      );

      return isDataValid
        ? response.data.content.sort((overlayA, overlayB) => overlayA.order - overlayB.order)
        : [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: OVERLAYS_FETCHING_ERROR_PREFIX }));
    }
  },
);

export const getAllUserOverlaysByCreator = createAsyncThunk<MapOverlay[], void, ThunkConfig>(
  'overlays/getAllUserOverlaysByCreator',
  async (_, { getState }) => {
    try {
      const state = getState() as RootState;
      const creator = state.user.login;
      if (!creator) return [];

      const response = await axiosInstanceNewAPI<PageOf<MapOverlay>>(
        apiPath.getAllUserOverlaysByCreatorQuery({
          creator,
          publicOverlay: false,
        }),
        {
          withCredentials: true,
        },
      );

      const isDataValid = validateDataUsingZodSchema(
        response.data,
        pageableSchema(mapOverlaySchema),
      );

      const sortByOrder = (userOverlayA: MapOverlay, userOverlayB: MapOverlay): number => {
        if (userOverlayA.order > userOverlayB.order) return 1;
        return -1;
      };

      const sortedUserOverlays = response.data.content
        .sort(sortByOrder)
        .filter(overlay => !overlay.publicOverlay);

      return isDataValid ? sortedUserOverlays : [];
    } catch (error) {
      return Promise.reject(getError({ error, prefix: USER_OVERLAYS_FETCHING_ERROR_PREFIX }));
    }
  },
);

/** UTILS */

type CreateFileArgs = {
  filename: string;
  content: string;
};

const createFile = async ({ filename, content }: CreateFileArgs): Promise<CreatedOverlayFile> => {
  const fileParams = {
    filename: `C:\\fakepath\\${filename}`,
    length: content.length.toString(),
  };

  const response = await axiosInstance.post(
    apiPath.createOverlayFile(),
    new URLSearchParams(fileParams),
    {
      withCredentials: true,
    },
  );

  const isDataValid = validateDataUsingZodSchema(response.data, createdOverlayFileSchema);
  return isDataValid ? response.data : undefined;
};

type UploadContentArgs = {
  createdFile: CreatedOverlayFile;
  overlayContent: string;
};
const uploadContent = async ({ createdFile, overlayContent }: UploadContentArgs): Promise<void> => {
  const data = new TextEncoder().encode(overlayContent);

  let uploadedLength = 0;

  const sendChunk = async (): Promise<void> => {
    if (uploadedLength >= data.length) {
      return;
    }

    const chunk = data.slice(uploadedLength, uploadedLength + CHUNK_SIZE);

    const response = await axiosInstance.post(
      apiPath.uploadOverlayFileContent(createdFile.id),
      chunk,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/octet-stream',
        },
      },
    );

    validateDataUsingZodSchema(response.data, uploadedOverlayFileContentSchema);

    uploadedLength += chunk.length;
    await sendChunk();
  };

  await sendChunk();
};

type CreatedOverlayArgs = {
  createdFile: CreatedOverlayFile;
  description: string;
  name: string;
  type: string;
  projectId: string;
  group: OverlayGroup;
};

const creteOverlay = async ({
  createdFile,
  description,
  type,
  name,
  projectId,
  group,
}: CreatedOverlayArgs): Promise<MapOverlay | undefined> => {
  const data = {
    name,
    description,
    filename: createdFile.filename,
    colorSchemaType: type,
    fileId: createdFile.id.toString(),
    group: group.id,
    public: false,
    orderIndex: 1,
  };

  const response = await axiosInstanceNewAPI.post<MapOverlay>(
    apiPath.createOverlay(projectId),
    data,
    {
      withCredentials: true,
    },
  );

  PluginsEventBus.dispatchEvent('onAddDataOverlay', response.data);

  const isDataValid = validateDataUsingZodSchema(response.data, mapOverlaySchema);

  return isDataValid ? response.data : undefined;
};

type AddOverlayArgs = {
  filename: string;
  content: string;
  description: string;
  type: string;
  name: string;
  projectId: string;
  group: OverlayGroup;
};

export const addOverlay = createAsyncThunk<undefined, AddOverlayArgs, ThunkConfig>(
  'overlays/addOverlay',
  async (
    { filename, content, description, name, type, projectId, group },
    { dispatch },
    // eslint-disable-next-line consistent-return
  ) => {
    try {
      const createdFile = await createFile({
        filename,
        content,
      });

      await uploadContent({
        createdFile,
        overlayContent: content,
      });

      await creteOverlay({
        createdFile,
        description,
        name,
        type,
        projectId,
        group,
      });

      await dispatch(getAllUserOverlaysByCreator());

      showToast({ type: 'success', message: USER_OVERLAY_ADD_SUCCESS_MESSAGE });
    } catch (error) {
      if (axios.isAxiosError(error) && error.code === 'ERR_BAD_REQUEST') {
        const data = error.response?.data;
        showToast({ type: 'error', message: data.reason || data.message, duration: 120000 });
      } else {
        return Promise.reject(getError({ error, prefix: USER_OVERLAY_ADD_ERROR_PREFIX }));
      }
    }
  },
);

export const updateOverlays = createAsyncThunk<undefined, MapOverlay[], ThunkConfig>(
  'overlays/updateOverlays',
  // eslint-disable-next-line consistent-return
  async userOverlays => {
    try {
      const userOverlaysPromises = userOverlays.map(userOverlay =>
        axiosInstanceNewAPI.put<MapOverlay>(
          apiPath.updateOverlay(userOverlay.id),
          {
            ...userOverlay,
            orderIndex: userOverlay.order,
            colorSchemaType: userOverlay.type,
            public: userOverlay.publicOverlay,
          },
          {
            withCredentials: true,
          },
        ),
      );

      const userOverlaysResponses = await Promise.all(userOverlaysPromises);

      const updatedUserOverlays = userOverlaysResponses.map(
        updatedUserOverlay => updatedUserOverlay.data,
      );

      validateDataUsingZodSchema(updatedUserOverlays, z.array(mapOverlaySchema));

      showToast({ type: 'success', message: USER_OVERLAY_UPDATE_SUCCESS_MESSAGE });
    } catch (error) {
      return Promise.reject(getError({ error, prefix: USER_OVERLAY_UPDATE_ERROR_PREFIX }));
    }
  },
);

export const removeOverlay = createAsyncThunk<undefined, { overlayId: number }, ThunkConfig>(
  'overlays/removeOverlay',
  // eslint-disable-next-line consistent-return
  async ({ overlayId }, { dispatch }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.removeProjectOverlay(PROJECT_ID, overlayId), {
        withCredentials: true,
      });

      PluginsEventBus.dispatchEvent('onRemoveDataOverlay', overlayId);
      dispatch(closeModal());
      dispatch(removeOverlayBioEntityForGivenOverlay({ overlayId }));
      await dispatch(getAllUserOverlaysByCreator());

      showToast({ type: 'success', message: USER_OVERLAY_REMOVE_SUCCESS_MESSAGE });
    } catch (error) {
      return Promise.reject(getError({ error, prefix: USER_OVERLAY_REMOVE_ERROR_PREFIX }));
    }
  },
);

type UpdateSingleOverlayArgs = {
  overlayId: number;
  overlayData: Partial<MapOverlay>;
  projectId: string;
};

export const updateSingleOverlay = createAsyncThunk<
  MapOverlay,
  UpdateSingleOverlayArgs,
  ThunkConfig
>('overlays/updateSingleOverlay', async ({ overlayId, overlayData, projectId }, { getState }) => {
  try {
    // Get the current overlay from state to have all required fields
    const state = getState() as RootState;
    const currentOverlay = state.overlays.projectOverlays.data?.find(o => o.id === overlayId);

    if (!currentOverlay) {
      throw new Error('Overlay not found in state');
    }

    // Check if creator is being changed
    let order = overlayData.order !== undefined ? overlayData.order : currentOverlay.order;
    if (overlayData.creator !== undefined && overlayData.creator !== currentOverlay.creator) {
      // Calculate new order for the new creator
      const creatorOverlays =
        state.overlays.projectOverlays.data?.filter(o => o.creator === overlayData.creator) || [];
      const INITIAL_ORDER = 1;
      order =
        creatorOverlays.length === 0
          ? INITIAL_ORDER
          : Math.max(...creatorOverlays.map(o => o.order)) + INITIAL_ORDER;
    }

    // Prepare the overlay object according to API requirements
    // Use current values as defaults, override with changed values
    const overlayPayload = {
      overlay: {
        id: overlayId,
        name: overlayData.name !== undefined ? overlayData.name : currentOverlay.name,
        description:
          overlayData.description !== undefined
            ? overlayData.description
            : currentOverlay.description,
        group: overlayData.group !== undefined ? overlayData.group : currentOverlay.group,
        publicOverlay:
          overlayData.publicOverlay !== undefined
            ? overlayData.publicOverlay
            : currentOverlay.publicOverlay,
        order,
        notMatchingAlpha:
          overlayData.notMatchingAlpha !== undefined
            ? overlayData.notMatchingAlpha
            : currentOverlay.notMatchingAlpha || null,
        type: overlayData.type !== undefined ? overlayData.type : currentOverlay.type,
        creator: overlayData.creator !== undefined ? overlayData.creator : currentOverlay.creator,
      },
    };

    const response = await axiosInstance.patch<MapOverlay>(
      apiPath.updateProjectOverlay(projectId, overlayId),
      overlayPayload,
      {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    const isDataValid = validateDataUsingZodSchema(response.data, mapOverlaySchema);
    if (!isDataValid) {
      throw new Error('Invalid overlay data received from server');
    }

    return response.data;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: USER_OVERLAY_UPDATE_ERROR_PREFIX }));
  }
});

type DownloadOverlaySourceArgs = {
  overlayId: number;
  overlayName: string;
  projectId: string;
};

export const downloadOverlaySource = createAsyncThunk<void, DownloadOverlaySourceArgs, ThunkConfig>(
  'overlays/downloadOverlaySource',
  async ({ overlayId, overlayName, projectId }) => {
    const response = await axiosInstance.get(apiPath.downloadProjectOverlay(projectId, overlayId), {
      responseType: 'blob',
      withCredentials: true,
    });

    // Create a download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${overlayName}_source.txt`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
);

type RemoveProjectOverlayArgs = {
  overlayId: number;
  projectId: string;
};

export const removeProjectOverlay = createAsyncThunk<
  undefined,
  RemoveProjectOverlayArgs,
  ThunkConfig
>('overlays/removeProjectOverlay', async ({ overlayId, projectId }, { dispatch }) => {
  try {
    await axiosInstanceNewAPI.delete(apiPath.removeProjectOverlay(projectId, overlayId), {
      withCredentials: true,
    });

    PluginsEventBus.dispatchEvent('onRemoveDataOverlay', overlayId);
    await dispatch(getAllProjectOverlays(projectId));

    // Success notification handled in component
    return undefined;
  } catch (error) {
    return Promise.reject(getError({ error, prefix: 'Failed to remove overlay' }));
  }
});

export const fetchOverlayTypes = createAsyncThunk<OverlayType[], void, ThunkConfig>(
  'overlays/fetchOverlayTypes',
  async () => {
    const response = await axiosInstanceNewAPI.get<OverlayType[]>(apiPath.getOverlayTypes());
    const isDataValid = validateDataUsingZodSchema(response.data, z.array(overlayTypeSchema));

    return isDataValid ? response.data : [];
  },
);
